import { AsyncBoundary } from '@/components/AsyncBoundary/AsyncBoundary';
import { Badge } from '@/components/Badge/Badge';
import { Card } from '@/components/Card/Card';
import { DataTable, type Column } from '@/components/DataTable/DataTable';
import { EmptyState } from '@/components/EmptyState/EmptyState';
import { PageHeader } from '@/components/PageHeader/PageHeader';
import { api } from '@/lib/api/endpoints';
import type { AuditEvent } from '@/lib/api/types';
import { formatDateTime } from '@/lib/format';
import { useAsync } from '@/lib/hooks/useAsync';
import styles from './pages.module.scss';

const OUTCOME_TONES = {
  denied: 'critical',
  failure: 'critical',
  allowed: 'positive',
  success: 'positive',
} as const;

/** US-6.2 — append-only trail, readable by admins and platform owners. */
export function AuditPage() {
  const { state, reload } = useAsync(
    () => api.audit.list({ limit: 100 }).then((result) => result.events),
    [],
  );

  const columns: ReadonlyArray<Column<AuditEvent>> = [
    { key: 'time', header: 'When', render: (event) => formatDateTime(event.occurred_at) },
    {
      key: 'actor',
      header: 'Actor',
      render: (event) => (
        <>
          {event.actor} <em className={styles.note}>{event.actor_role}</em>
        </>
      ),
    },
    { key: 'action', header: 'Action', render: (event) => <code>{event.action}</code> },
    {
      key: 'context',
      header: 'Context',
      secondary: true,
      render: (event) => event.cohort_id ?? event.source_id ?? '—',
    },
    {
      key: 'outcome',
      header: 'Outcome',
      render: (event) => (
        <Badge
          tone={OUTCOME_TONES[event.outcome as keyof typeof OUTCOME_TONES] ?? 'neutral'}
        >
          {event.outcome}
        </Badge>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        eyebrow="US-6.2"
        title="Audit log"
        description="Every auth decision, source sync, cohort change, access grant or revocation, and live-session transition. Records are append-only — they are never edited or deleted from here."
      />

      <Card title="Recent events">
        <AsyncBoundary state={state} subject="audit events" plannedUnder="US-6.2" onRetry={reload}>
          {(events) => (
            <DataTable
              caption="Audit events"
              columns={columns}
              rows={events}
              rowKey={(event) => String(event.id)}
              empty={<EmptyState title="No audit events recorded yet" />}
            />
          )}
        </AsyncBoundary>
      </Card>
    </>
  );
}
